// liveVehicleApi.js - API utilities for live vehicle telematics

import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

const API_BASE_URL = 'https://api.innoworx.ai/Stage';

const DEFAULT_DEVICES_LIMIT = 200;

/**
 * Fetches the ID token from AWS Amplify Auth.
 */
const getAuthToken = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    if (tokens && tokens.idToken) {
      const idToken = tokens.idToken;
      const currentTime = Math.floor(Date.now() / 1000);

      if (idToken.payload.exp <= currentTime) {
        console.error('ID Token has expired. User needs to re-authenticate.');
        return null;
      }

      console.log('Successfully retrieved valid ID token for live vehicle API');
      return tokens.idToken.toString();
    }

    console.error(
      'No ID Token found in the current session. User may not be logged in.'
    );
    return null;
  } catch (error) {
    console.error('Error fetching authentication session:', error);
    return null;
  }
};

/**
 * Makes an authenticated API request.
 * @param {string} endpoint - Path relative to API_BASE_URL.
 * @param {string} method - HTTP method.
 * @param {Object|null} body - Optional JSON body.
 * @returns {Promise<Response>}
 */
const makeApiRequest = async (endpoint, method = 'GET', body = null) => {
  const token = await getAuthToken();

  if (!token) {
    const error = new Error(
      'Authentication token is missing. Please ensure a user is logged in.'
    );
    error.code = 'AUTHENTICATION_FAILED';
    error.details =
      'No valid authentication token found. User may need to log in again.';
    throw error;
  }

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const config = {
    method,
    headers,
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  try {
    console.log(`Making ${method} request to: ${API_BASE_URL}${endpoint}`);
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(
        `API Error: ${response.status} ${response.statusText} for ${endpoint}`,
        errorData
      );

      const error = new Error(
        `API Error: ${response.status} - ${errorData.error || errorData.body?.error || 'Unknown error or no error message from API'}`
      );
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = endpoint;
      error.responseData = errorData;

      loggingService
        .logError(
          'api_error_live_vehicle',
          `Live Vehicle API error: ${response.status} ${response.statusText} for ${endpoint}`,
          error,
          {
            endpoint,
            method,
            status: response.status,
            statusText: response.statusText,
            responseData: errorData,
          }
        )
        .catch(logError => {
          console.warn('[liveVehicleApi] Failed to log error:', logError);
        });

      throw error;
    }

    return response;
  } catch (error) {
    if (error.code === 'AUTHENTICATION_FAILED') {
      throw error;
    }

    console.error(`Network or Fetch Error for ${endpoint}:`, error);
    error.endpoint = endpoint;
    error.method = method;

    loggingService
      .logError(
        'api_error_live_vehicle',
        `Live Vehicle API network/fetch error for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          errorType: error.name || 'NetworkError',
        }
      )
      .catch(logError => {
        console.warn('[liveVehicleApi] Failed to log error:', logError);
      });

    throw error;
  }
};

/**
 * API utilities for live vehicle telematics.
 */
export const liveVehicleApi = {
  /**
   * Fetches telematics devices (live vehicles).
   * GET /telematics/devices?limit={limit}&include_position={includePosition}
   *
   * With `include_position=true`, each item may include `last_position`:
   * `{ lat, lng, updated_at, speed, heading }` or `null` when no GPS fix.
   * Items with `last_position: null` should be omitted from the map.
   *
   * @param {Object} [options]
   * @param {number} [options.limit=200] - Max devices to return.
   * @param {boolean} [options.includePosition=true] - Include `last_position` on items.
   * @returns {Promise<{
   *   items: Array<{
   *     terminal_phone: string,
   *     tenant_id: string,
   *     registration_status: string,
   *     authenticated_at: string,
   *     last_connected_at: string,
   *     license_plate: string,
   *     manufacturer_id: string,
   *     vehicle_id: string,
   *     data_source: string,
   *     last_position?: {
   *       lat: number,
   *       lng: number,
   *       updated_at: string,
   *       speed: number,
   *       heading: number
   *     } | null
   *   }>,
   *   count: number
   * }>}
   *
   * @example
   * const { items, count } = await liveVehicleApi.getAllLiveVehicles();
   * const mappable = items.filter(d => d.last_position != null);
   */
  getAllLiveVehicles: async ({
    limit = DEFAULT_DEVICES_LIMIT,
    includePosition = true,
  } = {}) => {
    try {
      const safeLimit = Math.max(1, Number(limit) || DEFAULT_DEVICES_LIMIT);
      const params = new URLSearchParams({
        limit: String(safeLimit),
        include_position: String(Boolean(includePosition)),
      });
      const endpoint = `/telematics/devices?${params.toString()}`;

      console.log(
        'Live Vehicle API: Fetching telematics devices',
        { limit: safeLimit, includePosition }
      );

      const response = await makeApiRequest(endpoint, 'GET');
      const data = await response.json();

      console.log(
        'Live Vehicle API: Successfully fetched telematics devices',
        data
      );
      return data;
    } catch (error) {
      console.error(
        'Live Vehicle API: Error fetching telematics devices',
        error
      );
      throw error;
    }
  },

  /**
   * Fetches a single telematics device with connectivity, position, and decoded events.
   * Use for the vehicle detail panel (card fields).
   *
   * GET /telematics/devices/{terminal_phone}?decode={decode}
   *
   * UI mapping:
   * - License plate → `device.license_plate`
   * - Last connected → `device.last_connected_at`
   * - "WORKING" badge → `connectivity.receiving_data === true` or `connectivity.status === "ok"`
   * - Tracker serial → `recent_events[0].serial` (JT808 message serial)
   * - Speed / heading / altitude / recorded at → `last_position` (`speed_kmh`, `heading`, `altitude_m`, `recorded_at`)
   * - Mileage / fuel / recorder speed → latest `0x0200` event `parsed.extras` (when `decode=true`)
   * - Fuel is liters in extras, not % — display as L/OBD
   * - Vehicle image → not in telematics (fleet API later)
   *
   * @param {string} terminalPhone - Device terminal phone (e.g. `048050021728`).
   * @param {Object} [options]
   * @param {boolean} [options.decode=true] - Decode JT808 events for `parsed.extras`.
   * @returns {Promise<{
   *   terminal_phone: string,
   *   terminal_phone_resolved: string,
   *   device: {
   *     terminal_phone: string,
   *     tenant_id: string,
   *     registration_status: string,
   *     license_plate: string,
   *     vehicle_id: string,
   *     last_connected_at: string,
   *     manufacturer_id: string,
   *     data_source: string
   *   },
   *   connectivity: {
   *     ok: boolean,
   *     receiving_data: boolean,
   *     status: string,
   *     summary: string
   *   },
   *   last_position: {
   *     latitude: number,
   *     longitude: number,
   *     speed_kmh: number,
   *     heading: number,
   *     altitude_m: number,
   *     recorded_at: string,
   *     updated_at: string
   *   } | null,
   *   recent_events: Array<{
   *     stored_at: string,
   *     msg_id: string,
   *     serial: number,
   *     parsed: {
   *       latitude: number,
   *       longitude: number,
   *       speed_kmh: number,
   *       heading: number,
   *       altitude_m: number,
   *       extras: Array<Object>,
   *       extras_summary: string[]
   *     }
   *   }>
   * }>}
   *
   * @example
   * const detail = await liveVehicleApi.getTelematicsDevice('048050021728');
   * const isWorking = detail.connectivity?.receiving_data === true
   *   || detail.connectivity?.status === 'ok';
   * const trackerSerial = detail.recent_events?.[0]?.serial;
   */
  getTelematicsDevice: async (terminalPhone, { decode = true } = {}) => {
    try {
      if (!terminalPhone || !String(terminalPhone).trim()) {
        throw new Error('terminalPhone is required');
      }

      const resolvedPhone = String(terminalPhone).trim();
      const params = new URLSearchParams({
        decode: String(Boolean(decode)),
      });
      const endpoint = `/telematics/devices/${encodeURIComponent(resolvedPhone)}?${params.toString()}`;

      console.log('Live Vehicle API: Fetching telematics device', {
        terminalPhone: resolvedPhone,
        decode,
      });

      const response = await makeApiRequest(endpoint, 'GET');
      const data = await response.json();

      console.log(
        'Live Vehicle API: Successfully fetched telematics device',
        data
      );
      return data;
    } catch (error) {
      console.error(
        'Live Vehicle API: Error fetching telematics device',
        error
      );
      throw error;
    }
  },

  /**
   * Fetches fleet KPIs for a device (distance, trips, idling, harsh events, speed stats, stops, route, driver score).
   * GET /telematics/devices/{terminal_phone}/metrics
   *
   * @param {string} terminalPhone - JT808 terminal ID (e.g. `048050021728`).
   * @param {Object} [options]
   * @param {number} [options.hours=24] - Window length when `from`/`to` omitted.
   * @param {string} [options.from] - ISO8601 window start (`Z`).
   * @param {string} [options.to] - ISO8601 window end (`Z`).
   * @param {number} [options.limit=1000] - Max events scanned (cap 2000).
   * @param {number} [options.speedLimitKmh=80] - Overspeed threshold.
   * @param {number} [options.tripGapMinutes=5] - Stopped time before a trip ends.
   * @param {number} [options.idleGapMinutes=3] - Stationary time before idling counts.
   * @returns {Promise<{
   *   terminal_phone: string,
   *   terminal_phone_resolved: string,
   *   vehicle_id: string,
   *   window: { from: string, to: string, events_analyzed: number },
   *   metrics: Object
   * }>}
   */
  getDeviceMetrics: async (terminalPhone, options = {}) => {
    try {
      if (!terminalPhone || !String(terminalPhone).trim()) {
        throw new Error('terminalPhone is required');
      }

      const {
        hours = 24,
        from,
        to,
        limit = 1000,
        speedLimitKmh = 80,
        tripGapMinutes = 5,
        idleGapMinutes = 3,
      } = options;

      const resolvedPhone = String(terminalPhone).trim();
      const params = new URLSearchParams();

      if (from) {
        params.set('from', from);
        if (to) params.set('to', to);
      } else {
        params.set('hours', String(Math.max(1, Number(hours) || 24)));
      }

      const safeLimit = Math.min(2000, Math.max(1, Number(limit) || 1000));
      params.set('limit', String(safeLimit));
      params.set('speed_limit_kmh', String(Number(speedLimitKmh) || 80));
      params.set('trip_gap_minutes', String(Number(tripGapMinutes) || 5));
      params.set('idle_gap_minutes', String(Number(idleGapMinutes) || 3));

      const endpoint = `/telematics/devices/${encodeURIComponent(resolvedPhone)}/metrics?${params.toString()}`;

      console.log('Live Vehicle API: Fetching device metrics', {
        terminalPhone: resolvedPhone,
        hours: from ? undefined : hours,
        from,
        to,
      });

      const response = await makeApiRequest(endpoint, 'GET');
      const data = await response.json();

      console.log(
        'Live Vehicle API: Successfully fetched device metrics',
        data
      );
      return data;
    } catch (error) {
      console.error(
        'Live Vehicle API: Error fetching device metrics',
        error
      );
      throw error;
    }
  },

  /**
   * Fetches a dated tracker summary report (KPIs, route, optional PDF).
   * GET /telematics/devices/{terminal_phone}/report
   *
   * @param {string} terminalPhone - Tracker ID (e.g. `048050021728`).
   * @param {Object} options
   * @param {string} options.from - Window start (`YYYY-MM-DDTHH:MM:SS`, UTC).
   * @param {string} options.to - Window end (`YYYY-MM-DDTHH:MM:SS`, UTC).
   * @param {boolean} [options.includePdf=true] - Request presigned PDF URL.
   * @param {number} [options.limit=2000] - Max events scanned (cap 2000).
   * @param {boolean} [options.decode=true] - Decode JT808 payloads.
   * @param {number} [options.speedLimitKmh=80] - Overspeed threshold.
   * @param {number} [options.tripGapMinutes=5] - Trip segmentation gap.
   * @param {number} [options.idleGapMinutes=3] - Idling clustering gap.
   * @returns {Promise<Object>} Tracker summary report payload.
   */
  getDeviceReport: async (terminalPhone, options = {}) => {
    try {
      if (!terminalPhone || !String(terminalPhone).trim()) {
        throw new Error('terminalPhone is required');
      }

      const {
        from,
        to,
        includePdf = true,
        limit = 2000,
        decode = true,
        speedLimitKmh = 80,
        tripGapMinutes = 5,
        idleGapMinutes = 3,
      } = options;

      if (!from || !to) {
        throw new Error('from and to are required for device report');
      }

      const resolvedPhone = String(terminalPhone).trim();
      const params = new URLSearchParams({
        from,
        to,
        include_pdf: String(Boolean(includePdf)),
        decode: String(Boolean(decode)),
        limit: String(Math.min(2000, Math.max(1, Number(limit) || 2000))),
        speed_limit_kmh: String(Number(speedLimitKmh) || 80),
        trip_gap_minutes: String(Number(tripGapMinutes) || 5),
        idle_gap_minutes: String(Number(idleGapMinutes) || 3),
      });

      const endpoint = `/telematics/devices/${encodeURIComponent(resolvedPhone)}/report?${params.toString()}`;

      console.log('Live Vehicle API: Fetching device report', {
        terminalPhone: resolvedPhone,
        from,
        to,
        includePdf,
      });

      const response = await makeApiRequest(endpoint, 'GET');
      const data = await response.json();

      console.log(
        'Live Vehicle API: Successfully fetched device report',
        data
      );
      return data;
    } catch (error) {
      console.error(
        'Live Vehicle API: Error fetching device report',
        error
      );
      throw error;
    }
  },
};
